import { addEntities, addEntity, entityConfig, removeEntity, withEntities } from '@ngrx/signals/entities';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { computed, inject } from '@angular/core';
import { DateTimeUtil } from '@web/csl-lib-utils';
import { GlobalStore } from '../global/global.store';
import { HealthRecordDocumentsService } from '@web/csl-lib-services';
import { HealthRecordDocumentsState } from './health-record-documents.state';
import { HealthRecordDocumentUploadMetadata, StoredHealthRecordDocument } from '@web/csl-lib-models';
import { HealthRecordMetaStore } from '../health-record-meta/health-record-meta.store';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { patchState, signalStore, type, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';

const initialState: HealthRecordDocumentsState = {
  healthRecordId: '',
  isLoading: false,
  isSaving: false,
  error: null
};

const hrDocumentEntityConfig = entityConfig({
  entity: type<StoredHealthRecordDocument>(),
  collection: 'hrDocument',
  selectId: (document) => document.id
});

export const HealthRecordDocumentsStore = signalStore(
  withState(initialState),
  withEntities(hrDocumentEntityConfig),
  withComputed((store) => ({
    sortedDocuments: computed(() => { return store.hrDocumentEntities().sort((a, b) => DateTimeUtil.compareDates(a.documentDate, b.documentDate, true));
    }),
  })),
  withMethods((store,
               docService = inject(HealthRecordDocumentsService),
               messageService = inject(MessageService),
               globalStore = inject(GlobalStore),
               healthRecordMetaStore = inject(HealthRecordMetaStore)
  ) => ({
      loadAllMeta: rxMethod<{ clinicId?: number }>(
        pipe(
          switchMap((args) => {
            globalStore.setLoading();
            patchState(store, { isLoading: true });
            return docService.getAllMeta(args?.clinicId ?? null, healthRecordMetaStore.healthRecordId()).pipe(
              tap({
                next: (documents: StoredHealthRecordDocument[]) => {
                  patchState(store, addEntities(documents, hrDocumentEntityConfig), { healthRecordId: healthRecordMetaStore.healthRecordId() });
                },
                error: (e) => {
                  console.error(e);
                  messageService.add({ severity: 'error', summary: 'Error!', detail: `Error loading document list.`, life: 4000 });
                },
                finalize: () => {
                  globalStore.unsetLoading();
                  patchState(store, { isLoading: false });
                }
              })
            );
          })
        )
      ),

      upload: rxMethod<{ file: File, metadata: HealthRecordDocumentUploadMetadata, clinicId?: number | null }>(
        pipe(
          switchMap((args) => {
            patchState(store, { isLoading: true, isSaving: true, error: null });
            globalStore.setLoading();
            return docService.upload(store.healthRecordId(), args.file, args.metadata, args.clinicId ?? null)
              .pipe(
                catchError((serviceError: HttpErrorResponse) => {
                  let errorMessages: string[];
                  if(serviceError.error?.errors) {
                    errorMessages = Object.values(
                      serviceError.error.errors as Record<string, string[]>
                    ).flat();
                  } else {
                    errorMessages = [serviceError.error || 'An unknown error occurred.'];
                  }
                  patchState(store, { error: serviceError.message });
                  messageService.add({
                    severity: 'error',
                    summary: 'Error!',
                    detail: errorMessages[0],
                    life: 5000,
                  });
                  globalStore.unsetLoading();
                  patchState(store, { isLoading: false, isSaving: false });
                  return EMPTY;
                }),
              tap({
                next: (document: StoredHealthRecordDocument) => {
                  patchState(store, addEntity(document, hrDocumentEntityConfig));
                  messageService.add({ severity: 'success', summary: 'Success!', detail: `Document "${document.fileName}" uploaded.`, life: 4000 });
                },
                // error: (e: HttpErrorResponse) => {
                //   // Nothing to do here, error is handled in catchError
                // },
                finalize: () => {
                  globalStore.unsetLoading();
                  patchState(store, { isLoading: false, isSaving: false });
                }
              })
            );
          })
        )
      ),

      delete: rxMethod<{ clinicId?: number, document: StoredHealthRecordDocument }>(
        pipe(
          switchMap((args) => {
              globalStore.setLoading();
              patchState(store, { isLoading: true });
              return docService.delete(store.healthRecordId(), args.document.id, args?.clinicId ?? null).pipe(
                tap({
                  next: () =>
                  {
                    patchState(store, removeEntity(args.document.id, hrDocumentEntityConfig));
                    messageService.add({ severity: 'success', summary: 'Success!', detail: `Document "${args.document.fileName}" deleted.`, life: 3000 });
                  },
                  error: (e) => {
                    console.error(e);
                    messageService.add({ severity: 'error', summary: 'Error!', detail: `Error deleting document "${args.document.fileName}".`, life: 4000 });
                  },
                  finalize: () => {
                    globalStore.unsetLoading();
                    patchState(store, { isLoading: false });
                  }
                })
              );
            }
          ))
      ),

    }
  ))
);
